import { useEffect, useState } from "react";
import { ShieldAlert, ShieldCheck } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { api } from "@/lib/api";
import { PageHeader, Panel, useAdminSettings } from "@/pages/admin/shared";

const Stat = ({ label, value, testId }) => (
  <Panel className="p-4">
    <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">{label}</p>
    <p data-testid={testId} className="mt-1 font-display text-3xl font-bold tabular-nums text-maroon">{value ?? "—"}</p>
  </Panel>
);

export default function Dashboard() {
  const { settings, save } = useAdminSettings();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const load = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, []);

  const open = settings?.voting_open;
  const max = Math.max(1, ...(stats?.per_category || []).map((c) => c.votes));

  return (
    <div data-testid="admin-dashboard">
      <PageHeader title="Dashboard" sub="Live — refreshes every 5 seconds" />
      <div className={`grain mb-6 flex flex-wrap items-center gap-4 rounded-2xl p-5 text-ivory ${open ? "bg-forest" : "bg-maroon"}`}>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-ivory/70">Public voting</p>
          <p data-testid="voting-state-label" className="font-display text-3xl font-bold">{open ? "OPEN" : "CLOSED"}</p>
        </div>
        <Switch
          checked={!!open}
          disabled={!settings}
          onCheckedChange={(v) => save({ voting_open: v }, v ? "Voting opened" : "Voting closed")}
          data-testid="voting-open-switch"
          className="scale-125 data-[state=checked]:bg-gold"
        />
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Total votes" value={stats?.total_votes} testId="stat-total-votes" />
        <Stat label="Unique voters" value={stats?.unique_voters} testId="stat-unique-voters" />
        <Stat label="Submissions / hr" value={stats?.submissions_last_hour} testId="stat-submissions-hour" />
        <Stat label="Rejected attempts" value={stats?.fraud_attempts} testId="stat-fraud" />
      </div>

      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <Panel>
          <h2 className="mb-4 font-display text-xl font-bold text-maroon">Votes per category</h2>
          <ul className="space-y-3">
            {stats?.per_category.map((c) => (
              <li key={c.id} data-testid={`stat-category-${c.key}`}>
                <div className="flex justify-between text-sm">
                  <span className="font-medium">{c.name_en} <span className="font-bengali text-ink-muted">· {c.name_bn}</span>{!c.active && " (hidden)"}</span>
                  <span className="tabular-nums font-semibold">{c.votes}</span>
                </div>
                <div className="mt-1 h-2.5 overflow-hidden rounded-full bg-gold/15">
                  <div className="h-full rounded-full bg-gradient-to-r from-maroon to-gold transition-[width] duration-700" style={{ width: `${(c.votes / max) * 100}%` }} />
                </div>
              </li>
            ))}
          </ul>
        </Panel>
        <Panel>
          <div className="flex items-start gap-3">
            {settings?.otp_required ? <ShieldCheck className="h-6 w-6 text-forest" /> : <ShieldAlert className="h-6 w-6 text-saffron" />}
            <div className="flex-1">
              <h2 className="font-display text-lg font-bold text-maroon">OTP hardening</h2>
              <p className="text-xs text-ink-muted">Require an email/phone code before votes are accepted. Channel: <b>{settings?.otp_channel}</b></p>
            </div>
            <Switch checked={!!settings?.otp_required} disabled={!settings} onCheckedChange={(v) => save({ otp_required: v }, v ? "OTP required" : "OTP off")} data-testid="dashboard-otp-switch" />
          </div>
          <p className="mt-4 text-xs text-ink-muted">Fingerprint + IP limits can be bypassed (incognito, second browser). Flip this on if you see ballot-stuffing in the fraud log.</p>
        </Panel>
      </div>
    </div>
  );
}
